import firebase from "../../config/firebase";
import algoliasearch from "algoliasearch";
import { toast } from "react-toastify";
import { createNullCache } from "@algolia/cache-common";
const client = algoliasearch("GPYJEAVUBP", "b1f999bc5b264defe930762391d240c6", {
  responsesCache: createNullCache(),
});

export const fetchTables = (restaurantID) => async (dispatch) => {
  dispatch({ type: "FETCH_TABLES_LOADING", payload: true });
  try {
    const db = firebase.firestore();
    const restaurantRef = db.collection("table").doc(restaurantID);

    restaurantRef.onSnapshot(async (restaurantSnapshot) => {
      if (!restaurantSnapshot.exists) {
        dispatch({
          type: "FETCH_TABLES_SUCCESS",
          payload: { infoData: {}, tables: {} },
        });
        dispatch({ type: "FETCH_TABLES_LOADING", payload: false });
        return;
      }

      const infoData = {
        id: restaurantSnapshot.id,
        ...restaurantSnapshot.data(),
      };

      const currentTime = firebase.firestore.Timestamp.now();

      restaurantRef
        .collection("tables")
        .orderBy("expireAt", "asc")
        .where("expireAt", ">", currentTime)
        .orderBy("occupiedAt", "desc")
        .onSnapshot(
          async (tablesSnapshot) => {
            const tablesArray = await Promise.all(
              tablesSnapshot.docs.map(async (doc) => {
                const tableData = { id: doc.id, ...doc.data() };

                if (tableData.customerId) {
                  try {
                    const customerDoc = await db
                      .collection("users")
                      .doc(tableData.customerId)
                      .get();
                    tableData.customerName =
                      customerDoc.exists &&
                      customerDoc.data().type === "customer"
                        ? customerDoc.data().name
                        : "Unknown";
                  } catch (error) {
                    console.error(
                      `Error fetching customer for table ${doc.id}:`,
                      error
                    );
                    tableData.customerName = "Error fetching name";
                  }
                } else {
                  tableData.customerName = "No customer";
                }
                return tableData;
              })
            );

            const tables = tablesArray.reduce((acc, table) => {
              acc[table.id] = table;
              return acc;
            }, {});

            dispatch({
              type: "FETCH_TABLES_SUCCESS",
              payload: { infoData, tables },
            });
            dispatch({ type: "FETCH_TABLES_LOADING", payload: false });
          },
          (error) => {
            console.error("Error fetching tables snapshot:", error);
            dispatch({ type: "FETCH_TABLES_LOADING", payload: false });
          }
        );
    });
  } catch (error) {
    console.error("Error fetching tables:", error);
    toast.error("Failed to fetch tables");
    dispatch({ type: "FETCH_TABLES_LOADING", payload: false });
  }
};

export const addTables =
  (restaurantID, timer, totalTables) => async (dispatch) => {
    dispatch({ type: "ADD_TABLES_LOADING", payload: true });
    try {
      const db = firebase.firestore();
      const restaurantRef = db.collection("table").doc(restaurantID);
      const tablesCollection = restaurantRef.collection("tables");
      const restaurantSnapshot = await restaurantRef.get();
      let existingTotalTables = 0;

      if (restaurantSnapshot.exists) {
        const existingData = restaurantSnapshot.data();
        existingTotalTables = existingData.totalTables || 0;
      }
      const batch = db.batch();
      let canProceed = true;

      if (totalTables > existingTotalTables) {
        for (let i = existingTotalTables + 1; i <= totalTables; i++) {
          const tableDocRef = tablesCollection.doc(`table_${i}`);
          batch.set(tableDocRef, {
            tableNo: i,
            expireAt: null,
            customerId: null,
            occupiedAt: null,
          });
        }
      } else if (totalTables < existingTotalTables) {
        for (let i = existingTotalTables; i > totalTables; i--) {
          const tableDocRef = tablesCollection.doc(`table_${i}`);
          const tableSnap = await tableDocRef.get();

          if (tableSnap.exists) {
            const data = tableSnap.data();
            if (data.customerId || data.occupiedAt) {
              canProceed = false; // block update
              throw new Error(
                `Table ${i} is occupied. Cannot remove occupied tables.`
              );
            }
            batch.delete(tableDocRef);
          }
        }
      }

      if (canProceed) {
        await batch.commit();
        await restaurantRef.set({ timer, totalTables }, { merge: true });

        dispatch({
          type: "ADD_TABLES_SUCCESS",
          payload: { restaurantID, timer, totalTables },
        });
        toast.success("Tables updated successfully");
      }
      setTimeout(() => {
        dispatch({ type: "ADD_TABLES_LOADING", payload: false });
      }, 1000);
    } catch (error) {
      console.error("Error updating tables:", error);
      toast.error(error?.message || "Failed to update tables");
      dispatch({ type: "ADD_TABLES_LOADING", payload: false });
    }
  };

export const occupiedChange = (restaurantID, tableId) => async (dispatch) => {
  dispatch({
    type: "OCCUPIED_CHANGE_LOADING",
    payload: { tableId, loading: true },
  });
  try {
    const db = firebase.firestore();
    const tableRef = db
      .collection("table")
      .doc(restaurantID)
      .collection("tables")
      .doc(tableId);
    await tableRef.update({
      expireAt: null,
      customerId: null,
      occupiedAt: null,
    });
    toast.success("Table Updated Successfully");
  } catch (error) {
    console.error(`Error updating table ${tableId}:`, error);
    toast.error("Failed to update table");
  } finally {
    dispatch({
      type: "OCCUPIED_CHANGE_LOADING",
      payload: { tableId, loading: false },
    });
  }
};

export const getAllStaff =
  (ID, search, hitsPerPage, currentPage) => async (dispatch) => {
    dispatch(getAllStaffLoading(true));

    try {
      const index = client.initIndex("users");

      const response = await index.search(search, {
        filters: `(type:kitchen-admin OR type:kitchen-cook OR type:kitchen-waiter) AND restaurantID:${ID}`,
        hitsPerPage,
        page: currentPage,
      });

      const { hits, ...rest } = response;

      const updatedHits = await Promise.all(
        hits.map(async (staff) => {
          try {
            const querySnap = await firebase
              .firestore()
              .collection("permissions")
              .where("userID", "==", staff.objectID)
              .get();

            if (querySnap.empty) {
              return {
                ...staff,
                permissionId: null,
                permissions: {},
              };
            }
            const doc = querySnap.docs[0];
            const { userID, ...permissionsData } = doc.data();

            return {
              ...staff,
              permissionId: doc.id,
              permissions: permissionsData,
            };
          } catch (error) {
            console.error(
              `Error fetching permission for ${staff.objectID}:`,
              error
            );
            return {
              ...staff,
              permissionId: null,
              permissions: {},
            };
          }
        })
      );

      dispatch({
        type: "GET_ALL_STAFF",
        payload: updatedHits,
      });

      dispatch({
        type: "STAFF_DETAILS",
        payload: rest,
      });
    } catch (error) {
      console.error("Error fetching staff data:", error);
    } finally {
      dispatch(getAllStaffLoading(false));
    }
  };

export const updateStaff =
  (id, obj, permissionId, permissions, onSuccess) => async (dispatch) => {
    dispatch(staffUpdateLoader(true));

    try {
      const db = firebase.firestore();

      await db.collection("users").doc(id).update(obj);

      if (permissionId) {
        await db
          .collection("permissions")
          .doc(permissionId)
          .update({
            ...permissions,
          });
      }

      dispatch({
        type: "STAFF_UPDATED",
        payload: {
          id,
          obj,
          permissions,
        },
      });

      onSuccess();
      toast.success("Staff Detail updated successfully");
    } catch (err) {
      console.error(err.message);
      toast.error(err.message);
    } finally {
      dispatch(staffUpdateLoader(false));
    }
  };

export const deleteStaff =
  (id, permissionId, onSuccess) => async (dispatch) => {
    dispatch(staffDeleteLoader(true));
    try {
      await firebase
        .firestore()
        .collection("users")
        .doc(id)
        .delete()
        .then(async () => {
          await firebase
            .firestore()
            .collection("permissions")
            .doc(permissionId)
            .delete()
            .then(() => {
              dispatch({
                type: "STAFF_DELETED",
                payload: id,
              });
              dispatch(staffDeleteLoader(false));
              onSuccess();
              toast.success("Staff member has been deleted");
            })
            .catch((error) => {
              console.error("Error deleting permissions: ", error);
              dispatch(staffDeleteLoader(false));
            });
        })
        .catch((err) => {
          toast.error(err.message);
          dispatch(staffDeleteLoader(false));
        });
    } catch (err) {
      toast.error("An error occurred while deleting the staff.");
      dispatch(staffDeleteLoader(false));
    }
  };

const staffDeleteLoader = (data) => async (dispatch) => {
  dispatch({
    type: "STAFF_DELETE_LOADER",
    payload: data,
  });
};

const getAllStaffLoading = (data) => async (dispatch) => {
  dispatch({
    type: "GET_ALL_STAFF_LOADER",
    payload: data,
  });
};

const staffUpdateLoader = (data) => async (dispatch) => {
  dispatch({
    type: "STAFF_UPDATE_LOADER",
    payload: data,
  });
};
